import { useMemo } from 'react';
import { useQuery } from 'react-query';
import CallQueueAPI from '@frontend/api-call-queue';
import { scopedFilterAndSort } from '@frontend/filters';
import { Trans, useTranslation } from '@frontend/i18n';
import { usePhoneLocationFilterShallowStore, usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { Text } from '@frontend/design-system';
import { CreateCallQueueButton } from '../../components/call-queue/new/create-button';
import { DeleteCallQueueIconButton } from '../../components/call-queue/new/delete-button';
import { IndexTable } from '../../components/common/index-table';
import { PhoneSettingsPage } from '../../components/settings/settings-page';
import { WeaveHelpLink } from '../../components/weave-support-link';
import { queryKeys } from '../../query-keys';
import { usePersistentPagination } from './usePersistentPagination';

export function CallQueueIndex() {
  const { locationFilters, setLocationFilters } = usePhoneLocationFilterShallowStore(
    'locationFilters',
    'setLocationFilters'
  );
  const { settingsTenantLocation, globalAvailableLocationIds } = usePhoneSettingsShallowStore(
    'settingsTenantLocation',
    'globalAvailableLocationIds'
  );
  const { t } = useTranslation('phone');
  const { navigate } = useSettingsNavigate();
  const filteredLocations = locationFilters.callQueuesIndex;
  const [_pagination, _setPagination, paginationTableProps, paginationConfig] =
    usePersistentPagination('call-queues-pagination');

  const { data = [], isLoading } = useQuery({
    queryKey: [settingsTenantLocation?.phoneTenantId, ...queryKeys.settings.listCallQueue()],
    queryFn: () =>
      CallQueueAPI.List({ tenantId: settingsTenantLocation?.phoneTenantId ?? '' }).then((res) => res.callQueues),
    enabled: !!settingsTenantLocation?.phoneTenantId,
    staleTime: 5 * 60 * 1000,
    meta: {
      error: {
        message: 'CallQueueIndex:listCallQueuesQuery | Failed to fetch call queues',
        data: { tenantId: settingsTenantLocation?.phoneTenantId },
      },
    },
  });

  const filteredCallQueues = useMemo(
    () =>
      scopedFilterAndSort({
        data,
        scopeIds: globalAvailableLocationIds,
        filteredScopeIds: filteredLocations,
      }),
    [data, globalAvailableLocationIds, filteredLocations]
  );

  return (
    <PhoneSettingsPage
      action={settingsTenantLocation ? <CreateCallQueueButton tenantLocation={settingsTenantLocation} /> : null}
      title={t('Call Queues')}
      subtitle={
        <Trans t={t}>
          <Text as='span'>
            Call Queues allow your callers to wait until your team is able to answer their call. For more information
            see{' '}
          </Text>
          <WeaveHelpLink href='https://www.weavehelp.com/weavehelp/article/set-up-and-edit-a-call-queue' />.
        </Trans>
      }
      breadcrumbs={[
        { label: t('Phone'), to: '/phone/main' },
        { label: t('Call Queues'), to: '/phone/call-queues' },
      ]}
    >
      {settingsTenantLocation ? (
        <IndexTable
          {...paginationTableProps}
          labelsColumnPosition={2}
          tableConfig={{
            ...paginationConfig,
            colConfig: [
              {
                id: 'name',
                Header: t('Name'),
                accessor: (data) => data.name,
                sortType: 'string',
              },
              {
                id: 'count',
                Header: t('Devices'),
                accessor: (data) => data.devicesCount,
                cellRenderer: (value) => <Text>{t('{{count}} Devices', { count: value })}</Text>,
                sortType: 'number',
                width: 100,
              },
            ],
            data: filteredCallQueues,
            isLoading,
            emptyStateConfig: {
              type: 'sync_your_phone',
              header: t('No Call Queues'),
              description: () =>
                !!data?.length ? (
                  <Text textAlign='center'>{t('There are no Call Queues to display with this filter setting.')}</Text>
                ) : (
                  <Trans t={t}>
                    <Text textAlign='center'>You don't have any call queues yet. </Text>
                    <Text textAlign='center'>
                      Click{' '}
                      <Text as='span' weight='bold'>
                        Create Call Queue
                      </Text>{' '}
                      to get started.
                    </Text>
                  </Trans>
                ),
            },
          }}
          tenantLocation={settingsTenantLocation}
          idAccessor={(data) => data.callQueueId}
          nameAccessor={(data) => data.name}
          labelsAccessor={(data) => data.locationIds}
          filteredLocations={filteredLocations}
          setFilteredLocations={(locations) => {
            setLocationFilters({ callQueuesIndex: locations });
          }}
          DeleteIconButton={DeleteCallQueueIconButton}
          onRowClick={(row) => {
            navigate({ to: '/phone/call-queues/:id', params: { id: row.callQueueId } });
          }}
        />
      ) : (
        <div>{t('No Phone Tenant Selected')}</div>
      )}
    </PhoneSettingsPage>
  );
}
